// The sampled instruments (public/samples/gm), loaded the first time a score needs them. Each plays
// like a Tone.Sampler (triggerAttackRelease), but a held note loops for as long as it's written, which
// a Sampler can't do, and a struck or plucked one rings away by itself. They share one output, with a
// little room on it and a limiter, so a full orchestra doesn't clip.
import * as Tone from "tone";
import { baseOf, GM_SOUNDS, instrumentOf } from "./instruments";
import { midi } from "./piano";

const FOLDER = "/samples/gm";
let output = null;
function outputNode() {
  if (!output) {
    const limiter = new Tone.Limiter(-1).toDestination();
    const room = new Tone.Reverb({ decay: 1.8, wet: 0.14 }).connect(limiter);
    output = new Tone.Gain(0.62).connect(room);
  }
  return output;
}

const load = async (url) => {
  const response = await fetch(url);
  if (!response.ok) throw new Error(`Couldn't load ${url}`);
  return response;
};
const decode = async (url) => Tone.getContext().decodeAudioData(await (await load(url)).arrayBuffer());

class Voices {
  loaded = true;
  active = new Set();
  // Into this node (the DAW's track), or else the sounds' shared output.
  constructor(output = null) { this.output = output; }
  // One note of a sample, from `when`, for `length` seconds.
  start({ buffer, rate = 1, loopStart, loopEnd }, { when, length, velocity, attack = 0.004, release = 0.1, decay = null, gain = 1 }) {
    // An export plays its notes after Tone has gone back to the live context, so a track's own comes first.
    const context = this.output?.context || Tone.getContext(), source = context.createBufferSource(), envelope = context.createGain();
    source.buffer = buffer;
    source.playbackRate.value = rate;
    if (loopEnd > loopStart) Object.assign(source, { loop: true, loopStart, loopEnd });
    source.connect(envelope);
    Tone.connect(envelope, this.output || outputNode());
    const level = Math.pow(Math.min(1, Math.max(0, velocity)), 1.4) * gain;
    envelope.gain.setValueAtTime(0, when);
    envelope.gain.linearRampToValueAtTime(level, when + attack);
    if (decay) envelope.gain.setTargetAtTime(0, when + attack, decay / 3);
    // What rings away isn't stopped by its written length.
    const end = when + Math.max(length, decay ? decay : 0.02);
    envelope.gain.setTargetAtTime(0, end, release / 3);
    source.start(when);
    source.stop(end + release * 2.5);
    const voice = { source, envelope };
    this.active.add(voice);
    source.onended = () => { this.active.delete(voice); envelope.disconnect(); };
    return voice;
  }
  releaseAll() {
    const now = (this.output?.context || Tone.getContext()).currentTime;
    for (const { source, envelope } of this.active) {
      envelope.gain.cancelScheduledValues(now);
      envelope.gain.setTargetAtTime(0, now, 0.03);
      try { source.stop(now + 0.15); } catch {}
    }
  }
  dispose() { this.releaseAll(); }
}
const seconds = (duration) => typeof duration === "number" ? duration : Tone.Time(duration).toSeconds();
const HELD = 600;

// A pitched sound: `zones` say which sample each key plays.
export class PitchedSound extends Voices {
  held = new Map();
  constructor(zones, shape, output) { super(output); this.zones = zones; this.shape = shape; }
  play(pitch, when, length, velocity) {
    const number = midi(pitch), zone = this.zones.find(({ low, high }) => number >= low && number <= high) || this.zones[this.zones.length - 1];
    return this.start({ ...zone, rate: 2 ** ((100 * number - zone.cents) / 1200) }, { ...this.shape, when, length, velocity });
  }
  triggerAttackRelease(notes, duration, time, velocity = 0.7) {
    const when = time ?? Tone.now(), length = seconds(duration);
    for (const pitch of [notes].flat()) this.play(pitch, when, length, velocity);
  }
  // A key held down, until it's let go.
  triggerAttack(pitch, time, velocity = 0.7) { this.held.set(pitch, this.play(pitch, time ?? Tone.now(), HELD, velocity)); }
  triggerRelease(pitch, time) {
    const voice = this.held.get(pitch), when = time ?? Tone.now(), { release = 0.1 } = this.shape;
    if (!voice) return;
    this.held.delete(pitch);
    voice.envelope.gain.cancelScheduledValues(when);
    voice.envelope.gain.setTargetAtTime(0, when, release / 3);
    voice.source.stop(when + release * 2.5);
  }
}
// A percussion instrument's sounds, by where they're written on its staff (in the DAW, by their note).
export class KitSound extends Voices {
  constructor(hits, output) { super(output); this.hits = hits; }
  triggerAttackRelease(notes, duration, time, velocity = 0.7) {
    const when = time ?? Tone.now();
    for (const at of [notes].flat()) {
      const hit = this.hits.get(at);
      if (hit) this.start(hit, { when, length: hit.buffer.duration / hit.rate, velocity, gain: hit.gain, release: 0.05 });
    }
  }
}

// A sampled sound's zones, with their samples. The DAW plays them on its own tracks.
export async function loadZones(name) {
  const zones = await (await load(`${FOLDER}/${name}/zones.json`)).json();
  return Promise.all(zones.map(async (zone) => ({ ...zone, buffer: await decode(`${FOLDER}/${name}/${zone.file}`) })));
}
async function loadPitched(name) { return new PitchedSound(await loadZones(name), GM_SOUNDS[name]); }
let drumZones = null;
async function loadKit(sounds) {
  drumZones ||= load(`${FOLDER}/drums/zones.json`).then((response) => response.json());
  const zones = await drumZones;
  return new KitSound(new Map(await Promise.all(sounds.map(async ({ at, drum, rate = 1, gain = 1 }) => [at, { buffer: await decode(`${FOLDER}/drums/${zones[drum].file}`), rate: rate * zones[drum].rate, gain }]))));
}

const sounds = new Map(), loading = new Map();
const nameOf = (key) => { const item = instrumentOf(key); return item?.sounds ? `kit:${item.id}` : GM_SOUNDS[item?.sound]?.program != null ? item.sound : null; };
function loadByName(name, key) {
  if (!loading.has(name)) loading.set(name, (name.startsWith("kit:") ? loadKit(instrumentOf(key).sounds) : loadPitched(name)).then((sound) => { sounds.set(name, sound); return sound; })
    .catch((error) => { loading.delete(name); throw error; }));
  return loading.get(name);
}
// The sampled sound of a part, or a named one ("pizzicato"), once it's loaded.
export const partSound = (key, named) => sounds.get(named || nameOf(key)) || null;
// A part's sound, loading it if it hasn't been; null for the instruments with samplers of their own.
export const loadPartSound = async (key) => { const name = nameOf(key); return name ? loadByName(name, key) : null; };
// The sounds of these parts, and pizzicato for strings. Resolves when they're ready; what failed stays silent.
export async function loadPartSounds(keys) {
  const wanted = [...new Set(keys.map(baseOf))];
  if (wanted.some((key) => instrumentOf(key)?.family === "strings")) loadByName("pizzicato").catch(() => {});
  await Promise.allSettled(wanted.map(loadPartSound));
}
export const needsLoading = (keys) => keys.some((key) => { const name = nameOf(key); return name && !sounds.has(name); });
// Stops everything still sounding, as playback stops.
export function releaseSounds() { for (const sound of sounds.values()) sound.releaseAll(); }
